// The tasking half of the plan rail: the task chips a unit can be given, which order card each chip
// becomes for that kind of unit, how a drop on the map or on a graphic card turns into a target, and
// the template opening the "Suggested opening" chip fills in.
//
// The chips are deliberately the language of a task organization ("attack", "screen", "ISR") rather
// than the language of the order deck; the mapping to cards lives here so the rail never shows a
// player a card that unit cannot be given (spec §5, §8).

import { card } from '../sim/deck';
import { UNIT_DEFS } from '../sim/scenario';
import type { Grid } from '../sim/grid';
import type { CardId, Graphic, Scenario, Target, UnitType } from '../sim/types';
import type { PlanOrder } from '../state/store';
import { centroid, type DrawKind, type Pt } from '../map/draw';
import { gridRef } from './reports';

export type TaskId = 'attack' | 'move' | 'defend' | 'screen' | 'fires' | 'isr' | 'reserve';

export interface TaskMeta {
  id: TaskId;
  label: string;
  /** One sentence: what the unit will actually do at H-hour. */
  blurb: string;
  color: string;
}

export const TASKS: TaskMeta[] = [
  { id: 'attack', label: 'attack', blurb: 'Close with the objective and fight through what is on it.', color: 'var(--color-friendly)' },
  { id: 'move', label: 'move', blurb: 'Move there without seeking a fight.', color: 'var(--color-friendly)' },
  { id: 'defend', label: 'defend', blurb: 'Occupy that ground and hold it.', color: 'var(--color-friendly)' },
  { id: 'screen', label: 'screen', blurb: 'Watch that graphic and report; do not become decisively engaged.', color: 'var(--color-friendly)' },
  { id: 'fires', label: 'fires', blurb: 'Plan a fire mission onto that point, fired at H-hour.', color: 'var(--color-fires)' },
  { id: 'isr', label: 'ISR', blurb: 'Intelligence, surveillance and reconnaissance: put eyes on it — a scout reconnoiters, a drone launches.', color: 'var(--color-isr)' },
  { id: 'reserve', label: 'reserve', blurb: 'No opening order. The unit waits in the assembly area for a FRAGO (fragmentary order).', color: 'var(--color-command)' },
];

/**
 * The one- or three-letter name of each drawing tool, for the compact tool strip at the top of the
 * plan rail (pitch 4). The long label and the expansion still ride on the card's title.
 */
export const TOOL_SHORT: Record<DrawKind, string> = {
  axis: 'axis',
  obj: 'obj',
  pl: 'PL',
  nai: 'NAI',
};

export function taskMeta(id: TaskId): TaskMeta {
  const t = TASKS.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown task: ${id}`);
  return t;
}

/**
 * The order card a task chip becomes for this kind of unit. ISR splits by airframe: a drone has to be
 * launched before it sees anything, a scout is given a route to reconnoiter. `reserve` deliberately
 * has no card — it is the absence of an opening order.
 */
export function cardForTask(task: TaskId, type: UnitType): CardId | null {
  switch (task) {
    case 'attack': return 'attack';
    case 'move': return 'move';
    case 'defend': return 'defend';
    case 'screen': return 'screen';
    case 'fires': return 'fireMission';
    case 'isr': return type === 'uas' ? 'launchUas' : 'recon';
    case 'reserve': return null;
  }
}

/**
 * What a unit is given when the planning clock runs out with its task picker still open.
 *
 * The picker used to be thrown away at zero: the player had dropped a platoon on a graphic and was
 * mid-decision, and the plan went to H-hour without it, silently. A default is a worse answer than the
 * one the player was about to give and a much better one than nothing - a maneuver platoon attacks,
 * a scout goes and looks, and anything else moves to where it was dropped.
 */
export function defaultTask(type: UnitType): TaskId {
  if (type === 'armor' || type === 'mech' || type === 'inf') return 'attack';
  if (type === 'recon' || type === 'uas') return 'isr';
  return 'move';
}

// ---- drops -> targets --------------------------------------------------------------------------------

/** What the unit card was dropped on: a control graphic, or bare ground. */
export type Drop =
  | { kind: 'graphic'; graphic: Graphic }
  | { kind: 'point'; at: Pt };

/**
 * A cell the unit could actually be sent to: the dropped cell if it is passable, otherwise the nearest
 * passable cell in a widening ring. Without this, a phase line midpoint that happens to land in a wash
 * or on a cliff would refuse every maneuver task with "Unreachable terrain".
 */
export function nearestPassableCell(grid: Grid, cell: number): number {
  if (grid.passable(cell)) return cell;
  const cx = grid.cx(cell), cy = grid.cy(cell);
  for (let r = 1; r < 40; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = cx + dx, y = cy + dy;
        if (!grid.inBounds(x, y)) continue;
        const i = grid.index(x, y);
        if (grid.passable(i)) return i;
      }
    }
  }
  return cell;
}

/**
 * Turns a drop into the target the card needs. A card that wants a graphic refuses a drop on bare
 * ground (with the reason the rail shows); a card that wants a point takes the centre of the graphic
 * it was dropped on.
 */
export function targetForDrop(cardId: CardId, drop: Drop, grid: Grid): { ok: true; target: Target } | { ok: false; reason: string } {
  const def = card(cardId);
  switch (def.targetKind) {
    case 'graphic': {
      if (drop.kind !== 'graphic') {
        return { ok: false, reason: `${def.label} needs a control graphic — drop the unit on a graphic card, or on a graphic on the map` };
      }
      return { ok: true, target: { kind: 'graphic', graphicId: drop.graphic.id } };
    }
    case 'point': {
      const at = drop.kind === 'point' ? drop.at : aimPoint(drop.graphic);
      const cell = grid.cellOfCm(at.x, at.y);
      return {
        ok: true,
        target: { kind: 'point', cell: def.family === 'maneuver' ? nearestPassableCell(grid, cell) : cell },
      };
    }
    default:
      return { ok: false, reason: `${def.label} cannot be planned from the rail` };
  }
}

/** Where a point-targeted card aims when it is dropped on a graphic: the end of an axis, the centre of anything else. */
export function aimPoint(g: Graphic): Pt {
  if (g.kind === 'axis' && g.pts.length > 0) return g.pts[g.pts.length - 1];
  return centroid(g.pts);
}

/**
 * Display-only fan-out for the task organization sitting in its assembly area.
 *
 * `fitScenario` packs the blue force into a 2 km strip, which at the planning camera's zoom puts several
 * 22-pixel symbols on top of each other, and a symbol you cannot see is a symbol you cannot task. This
 * nudges any pair closer than `minSepCm` apart, in AO centimeters, by relaxation - a few passes, each
 * pushing both members of an overlapping pair half of the overlap along the line between them.
 *
 * It moves pixels, never the simulation: the scenario's own placements are what the worker is handed at
 * H-hour, and this runs on a copy for the overlay. A larger separation would need the assembly area
 * itself to be laid out differently, which is `src/sim/scenario.ts`'s business.
 */
export function spreadForDisplay(pts: readonly Pt[], minSepCm: number, passes = 8): Pt[] {
  const out = pts.map((p) => ({ x: p.x, y: p.y }));
  for (let pass = 0; pass < passes; pass++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= minSepCm) continue;
        if (d < 1) {
          // Exactly coincident: separate along a deterministic angle so the same scenario always
          // fans out the same way.
          const ang = ((i * 7 + j * 3) % 12) * (Math.PI / 6);
          dx = Math.cos(ang);
          dy = Math.sin(ang);
          d = 1;
        }
        const push = (minSepCm - d) / 2;
        const ux = dx / d;
        const uy = dy / d;
        a.x -= ux * push;
        a.y -= uy * push;
        b.x += ux * push;
        b.y += uy * push;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return out.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }));
}

// ---- reading a plan back ------------------------------------------------------------------------------

/** "attack → OBJ COBALT" / "move → grid 4568", for the plan card in the rail. The point form is the
 * four-digit grid reference the staff actually says on the net (src/ui/reports.ts `gridRef`), not the
 * terrain grid's raw cell indices. */
export function describeTarget(target: Target, graphics: Graphic[], grid: Grid | null): string {
  if (target.kind === 'graphic') {
    return graphics.find((g) => g.id === target.graphicId)?.name ?? 'a graphic that is gone';
  }
  if (target.kind === 'point' && target.cell !== undefined && grid) {
    return `grid ${gridRef(target.cell, grid)}`;
  }
  return 'no target';
}

/**
 * Spacing, in AO meters, for the planning stage's reporting-grid overlay (pitch 4).
 *
 * "grid 5071" used to resolve nowhere: the number was printed in the rail and in every report, and
 * nothing on the map said where the fiftieth easting was. A four-digit reference divides the AO into
 * 100 x 100 squares whatever its cell size (`gridRef` above), so this returns the whole number of
 * those squares closest to one kilometre — 1,050 m on the 15 km NTC area of operations. Every line
 * drawn at that spacing is a real grid boundary, so each one can be labelled with the two digits it
 * contributes, and a reference read off the rail lands in a square the player can point at.
 */
export function gridOverlayCellM(grid: Grid): number {
  const squareM = (grid.w * grid.cellM) / 100;
  return Math.max(1, Math.round(1000 / squareM)) * squareM;
}

// ---- the suggested opening ----------------------------------------------------------------------------

/**
 * A sensible opening a planner can start from and edit: two armor and two mech platoons attack **the
 * objective**, a scout reconnoiters the first named area of interest, and a drone launches over the
 * second. Artillery is left unassigned on purpose — where the first fire mission goes is the decision
 * the planner should be making.
 *
 * The maneuver task is aimed at the objective graphic, not at the end of the axis of advance. It used
 * to aim at the axis (or, with no axis drawn, at the phase line), which is how the suggestion came to
 * seize the objective 0 times in 20 in the wind tunnel: the axis is the *route*, and its last point
 * sits wherever the planner stopped clicking — short of the objective, by definition, since the
 * objective is the thing at the end of it. The same plan aimed at the objective seizes it every time.
 *
 * The axis is still honoured as a route hint: a drop on it is how a planner says "go this way", and
 * the drawn graphic goes into the simulation with the scenario either way. It is simply not the aim
 * point of the attack.
 */
export function suggestedOpening(scenario: Scenario, grid: Grid): PlanOrder[] {
  const plan: PlanOrder[] = [];
  const graphics = scenario.graphics;
  const objective = graphics.find((g) => g.id === scenario.objectiveId);
  const axis = graphics.find((g) => g.kind === 'axis');
  const plWhite = graphics.find((g) => g.kind === 'pl' && /WHITE/i.test(g.name)) ?? graphics.find((g) => g.kind === 'pl');
  // The same path a hand-made task takes: `attack` is a point-targeted card, so a drop on the
  // objective graphic resolves through `targetForDrop` to the nearest passable cell of its centre.
  const aimAt = objective ?? axis ?? plWhite ?? null;

  const byType = (type: UnitType, n: number): number[] =>
    scenario.blue
      .map((p, id) => ({ p, id }))
      .filter((u) => u.p.type === type)
      .slice(0, n)
      .map((u) => u.id);

  if (aimAt) {
    const target = targetForDrop('attack', { kind: 'graphic', graphic: aimAt }, grid);
    if (target.ok) {
      for (const id of [...byType('armor', 2), ...byType('mech', 2)]) {
        plan.push({ unitId: id, card: 'attack', target: target.target });
      }
    }
  }

  const nais = graphics.filter((g) => g.kind === 'nai');
  const scout = byType('recon', 1)[0];
  if (scout !== undefined && nais[0]) {
    plan.push({ unitId: scout, card: 'recon', target: { kind: 'graphic', graphicId: nais[0].id } });
  }
  const drone = byType('uas', 1)[0];
  const watch = nais[1] ?? nais[0];
  if (drone !== undefined && watch) {
    const c = centroid(watch.pts);
    plan.push({ unitId: drone, card: 'launchUas', target: { kind: 'point', cell: grid.cellOfCm(c.x, c.y) } });
  }

  return plan;
}

// ---- task organization for the rail ---------------------------------------------------------------------

export interface RailUnit { id: number; callsign: string; type: UnitType; label: string; sidc: string }
export interface RailGroup { key: string; title: string; units: RailUnit[] }

/** Blue's units grouped the way the rail lists them: maneuver companies by letter, then the enablers. */
export function railGroups(scenario: Scenario): RailGroup[] {
  const groups = new Map<string, RailUnit[]>();
  scenario.blue.forEach((p, id) => {
    const def = UNIT_DEFS[p.type];
    const key = /^[A-Z]\d\d$/.test(p.callsign) ? p.callsign[0] : 'TF';
    const unit: RailUnit = { id, callsign: p.callsign, type: p.type, label: def.label, sidc: def.sidcBlue };
    const list = groups.get(key);
    if (list) list.push(unit);
    else groups.set(key, [unit]);
  });
  return [...groups.entries()].map(([key, units]) => ({
    key,
    title: key === 'TF' ? 'Task force troops' : `Company ${key}`,
    units,
  }));
}
